import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { campusApi } from "./campus-api";
import { Node, Camera, CreateCameraDto } from "./types";
import CameraFormModal from "./components/CameraFormModal";
import CameraLiveFeedModal from "./components/CameraLiveFeedModal";

export default function NodeDetail() {
  const { campusId, nodeId } = useParams<{ campusId: string; nodeId: string }>();
  const navigate = useNavigate();
  const [node, setNode] = useState<Node | null>(null);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [liveFeedModalOpen, setLiveFeedModalOpen] = useState(false);
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);

  useEffect(() => {
    if (nodeId) {
      fetchNode();
      fetchCameras();
    }
  }, [nodeId]);

  const fetchNode = async () => {
    if (!nodeId) return;
    try {
      const data = await campusApi.getNodeById(nodeId);
      setNode(data);
    } catch (error: any) {
      toast.error("Failed to load node");
    }
  };

  const fetchCameras = async () => {
    if (!nodeId) return;
    setLoading(true);
    try {
      const data = await campusApi.getCamerasByNode(Number(nodeId));
      setCameras(data);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load cameras");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCamera(null);
    setModalOpen(true);
  };

  const handleEdit = (camera: Camera) => {
    setEditingCamera(camera);
    setModalOpen(true);
  };

  const handleDelete = async (cameraId: number) => {
    if (!window.confirm("Are you sure you want to delete this camera?")) {
      return;
    }

    try {
      await campusApi.deleteCamera(cameraId);
      toast.success("Camera deleted successfully");
      fetchCameras();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete camera");
    }
  };

  const handleViewLiveFeed = async (camera: Camera) => {
    setSelectedCamera(camera);
    setLiveFeedModalOpen(true);
  };

  const handleToggleVehicleDetection = async (camera: Camera) => {
    try {
      await campusApi.updateCamera(camera.id, {
        vehicleDetectionEnabled: !camera.vehicleDetectionEnabled,
      });
      toast.success(`Vehicle detection ${!camera.vehicleDetectionEnabled ? "enabled" : "disabled"}`);
      fetchCameras();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update camera");
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCamera(null);
    fetchCameras();
  };

  if (!nodeId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Invalid node ID
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate(`/campuses/${campusId}/nodes`)}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4 flex items-center gap-2"
              >
                <i className="fa-solid fa-arrow-left"></i>
                <span>Back to Nodes</span>
              </button>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Node: {node?.node_name || "Loading..."}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Node ID: <span className="font-mono">{node?.id || "-"}</span>
              </p>
            </div>
            <button
              onClick={handleCreate}
              className="btn-primary px-4 py-2 flex items-center space-x-2"
            >
              <i className="fa-solid fa-plus"></i>
              <span>Add Camera</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : cameras.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No cameras found. Click "Add Camera" to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cameras.map((camera) => (
                <div
                  key={camera.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                        Camera #{camera.id}
                      </h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        camera.type === "entry"
                          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          : camera.type === "exit"
                          ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                          : "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                      }`}>
                        {camera.type === "entry" ? "Entry" : camera.type === "exit" ? "Exit" : "Both"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewLiveFeed(camera)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                        title="View live feed"
                      >
                        <i className="fa-solid fa-video"></i>
                      </button>
                      <button
                        onClick={() => handleEdit(camera)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                        title="Edit camera"
                      >
                        <i className="fa-solid fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(camera.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                        title="Delete camera"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">RTSP URL</p>
                      <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                        {camera.rtspUrl}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <label className="text-sm text-gray-700 dark:text-gray-300">
                        Vehicle Detection
                      </label>
                    </div>
                    <button
                      onClick={() => handleToggleVehicleDetection(camera)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        camera.vehicleDetectionEnabled
                          ? "bg-green-500"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          camera.vehicleDetectionEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CameraFormModal
        open={modalOpen}
        onClose={handleModalClose}
        camera={editingCamera}
        nodeId={nodeId}
      />

      <CameraLiveFeedModal
        open={liveFeedModalOpen}
        onClose={() => {
          setLiveFeedModalOpen(false);
          setSelectedCamera(null);
        }}
        camera={selectedCamera}
      />
    </div>
  );
}

